import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Upload, FileSpreadsheet, BarChart3, Search, Loader,
  Lightbulb, Send, Trash2, Eye, MessageSquare, TrendingUp, X,
} from 'lucide-react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, ArcElement,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#987D9C', '#768597', '#BCA182', '#2d6a3d', '#8b3a3a', '#4a7c59', '#6b5b95', '#d4a574'];
const CHART_BG = ['#F9E8FA', '#E4EDF8', '#FEEFDC', '#b8d1ba', '#FCC9C7', '#d4edda', '#e8dff5', '#fce4c6'];

const InsightExplorer = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.explorer;
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const [view, setView] = useState('upload'); // upload | dashboard | query
  const [datasets, setDatasets] = useState([]);
  const [activeDataset, setActiveDataset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Query chat
  const [queryMessages, setQueryMessages] = useState([]);
  const [queryInput, setQueryInput] = useState('');
  const [querying, setQuerying] = useState(false);
  const [querySessionId, setQuerySessionId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  usePublishToEmma('AI Insight Explorer', analysis);

  useEffect(() => { loadDatasets(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [queryMessages]);

  const loadDatasets = async () => {
    try {
      const res = await axios.get(`${API}/compass/explorer/datasets`, { headers });
      setDatasets(res.data.datasets);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      alert('Only CSV and Excel files are supported.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/compass/explorer/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setActiveDataset(res.data);
      setView('dashboard');
      setAnalysis(null);
      setQueryMessages([]);
      setQuerySessionId(null);
      loadDatasets();
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const loadDataset = async (datasetId) => {
    try {
      const res = await axios.get(`${API}/compass/explorer/dataset/${datasetId}`, { headers });
      setActiveDataset(res.data);
      setView('dashboard');
      setAnalysis(null);
      setQueryMessages([]);
      setQuerySessionId(null);
    } catch (e) { console.error(e); }
  };

  const runAnalysis = async () => {
    if (!activeDataset) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/compass/explorer/analyze`, { dataset_id: activeDataset.dataset_id }, { headers });
      setAnalysis(res.data.analysis);
    } catch (e) {
      alert('Analysis failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const sendQuery = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || querying) return;
    setQueryMessages(prev => [...prev, { role: 'user', content: text }]);
    setQueryInput('');
    setQuerying(true);
    try {
      const res = await axios.post(`${API}/compass/explorer/query`, {
        dataset_id: activeDataset.dataset_id,
        question: text,
        session_id: querySessionId,
      }, { headers });
      setQuerySessionId(res.data.session_id);
      setQueryMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch (e) {
      setQueryMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Sorry, I could not answer that. Please try again.' : 'Sorry, ik kon dat niet beantwoorden. Probeer het opnieuw.' }]);
    } finally {
      setQuerying(false);
    }
  };

  const renderChart = (chart, idx) => {
    const colors = chart.datasets[0]?.data?.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
    const bgColors = chart.datasets[0]?.data?.map((_, i) => CHART_BG[i % CHART_BG.length]);

    const data = {
      labels: chart.labels,
      datasets: (Array.isArray(chart.datasets) ? chart.datasets : []).map(ds => ({
        ...ds,
        backgroundColor: chart.type === 'doughnut' ? bgColors : (chart.type === 'bar' ? bgColors : 'rgba(118, 133, 151, 0.15)'),
        borderColor: chart.type === 'doughnut' ? colors : '#768597',
        borderWidth: chart.type === 'doughnut' ? 2 : 2,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: '#768597',
      })),
    };

    const opts = { responsive: true, maintainAspectRatio: true, plugins: { legend: { display: chart.type === 'doughnut' } } };
    const Component = chart.type === 'bar' ? Bar : chart.type === 'doughnut' ? Doughnut : Line;
    return (
      <div key={idx} className="explorer-chart-card" data-testid={`chart-${idx}`}>
        <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{chart.title}</h4>
        <Component data={data} options={opts} />
      </div>
    );
  };

  // ─── Upload View ───
  if (view === 'upload') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><Search size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="explorer-upload-section" data-testid="upload-section">
            <div
              className={`explorer-dropzone ${dragOver ? 'explorer-dropzone-active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="dropzone"
            >
              {uploading ? (
                <><Loader className="spin" size={32} /><p className="body-medium">{lang === 'nl' ? 'Uploaden & analyseren...' : 'Uploading & analyzing...'}</p></>
              ) : (
                <>
                  <Upload size={40} />
                  <h3 className="heading-3">{tp.dropzoneText}</h3>
                  <p className="body-small text-secondary">{lang === 'nl' ? 'Ondersteunt CSV, Excel (.xlsx, .xls) — Max 10MB' : 'Supports CSV, Excel (.xlsx, .xls) — Max 10MB'}</p>
                </>
              )}
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                onChange={(e) => handleUpload(e.target.files[0])} data-testid="file-input" />
            </div>

            {datasets.length > 0 && (
              <div className="explorer-history" data-testid="dataset-history">
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{lang === 'nl' ? 'Vorige Uploads' : 'Previous Uploads'}</h3>
                {(Array.isArray(datasets) ? datasets : []).map(ds => (
                  <button key={ds.dataset_id} className="explorer-history-item" onClick={() => loadDataset(ds.dataset_id)} data-testid={`dataset-${ds.dataset_id}`}>
                    <FileSpreadsheet size={18} />
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <span className="body-small" style={{ fontWeight: 500 }}>{ds.filename}</span>
                      <span className="caption">{ds.rows} {lang === 'nl' ? 'rijen' : 'rows'} · {ds.columns.length} {lang === 'nl' ? 'kolommen' : 'columns'}</span>
                    </div>
                    <Eye size={16} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Dashboard View ───
  return (
    <div className="explorer-page" data-testid="explorer-dashboard">
      {/* Sidebar */}
      <aside className="compass-sidebar">
        <div className="compass-sidebar-header">
          <button onClick={() => setView('upload')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
            <ArrowLeft size={14} /> {t.common.back}
          </button>
        </div>

        <div className="compass-role-badge" style={{ background: 'var(--accent-blue-200)' }}>
          <FileSpreadsheet size={16} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="body-small" style={{ fontWeight: 500, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeDataset?.filename}
            </span>
            <span className="caption">{activeDataset?.rows} {lang === 'nl' ? 'rijen' : 'rows'}</span>
          </div>
        </div>

        <nav className="compass-sidebar-nav">
          <button className={`compass-nav-item ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')} data-testid="tab-dashboard">
            <BarChart3 size={16} /> Dashboard
          </button>
          <button className={`compass-nav-item ${view === 'query' ? 'active' : ''}`} onClick={() => setView('query')} data-testid="tab-query">
            <MessageSquare size={16} /> {lang === 'nl' ? 'Stel Vragen' : 'Ask Questions'}
          </button>
        </nav>

        <button className="compass-new-chat" onClick={() => { setView('upload'); setActiveDataset(null); }} data-testid="new-upload-btn">
          <Upload size={14} /> {lang === 'nl' ? 'Nieuwe Upload' : 'New Upload'}
        </button>
      </aside>

      {/* Main */}
      <main className="compass-main">
        {view === 'dashboard' && activeDataset && (
          <div className="explorer-dashboard-content">
            {/* Data Summary */}
            <div className="explorer-summary-bar" data-testid="data-summary">
              <div className="explorer-stat">
                <span className="caption">ROWS</span>
                <span className="heading-3">{activeDataset.rows?.toLocaleString()}</span>
              </div>
              <div className="explorer-stat">
                <span className="caption">COLUMNS</span>
                <span className="heading-3">{activeDataset.columns?.length}</span>
              </div>
              <div className="explorer-stat">
                <span className="caption">NUMERIC</span>
                <span className="heading-3">{activeDataset.summary?.numeric_columns?.length || 0}</span>
              </div>
              <div className="explorer-stat">
                <span className="caption">TEXT</span>
                <span className="heading-3">{activeDataset.summary?.text_columns?.length || 0}</span>
              </div>
            </div>

            {/* Charts */}
            {activeDataset.charts?.length > 0 && (
              <div className="explorer-charts-grid" data-testid="charts-section">
                {(Array.isArray(activeDataset.charts) ? activeDataset.charts : []).map((chart, i) => renderChart(chart, i))}
              </div>
            )}

            {/* AI Analysis */}
            <div className="explorer-analysis-section" data-testid="analysis-section">
              {!analysis ? (
                <div className="explorer-analysis-cta">
                  <Lightbulb size={28} />
                  <h3 className="heading-3">{lang === 'nl' ? 'Ontdek Verborgen Inzichten' : 'Discover Hidden Insights'}</h3>
                  <p className="body-small text-secondary">{lang === 'nl' ? 'Laat AI je data analyseren en patronen, kansen en aanbevelingen onthullen.' : 'Let AI analyze your data and reveal patterns, opportunities, and recommendations.'}</p>
                  <button className="btn-primary" onClick={runAnalysis} disabled={analyzing} data-testid="analyze-btn">
                    {analyzing ? <><Loader className="spin" size={16} /> {t.common.analyzing}</> : <><TrendingUp size={16} /> {lang === 'nl' ? 'Start AI Analyse' : 'Run AI Analysis'}</>}
                  </button>
                </div>
              ) : (
                <div className="explorer-analysis-result" data-testid="analysis-result">
                  <div className="explorer-analysis-header">
                    <h3 className="heading-3">{lang === 'nl' ? 'AI Inzichten' : 'AI Insights'}</h3>
                    <button className="btn-secondary" onClick={() => setAnalysis(null)} style={{ fontSize: '0.75rem' }}>
                      <X size={14} /> {lang === 'nl' ? 'Wissen' : 'Clear'}
                    </button>
                  </div>
                  <div className="markdown-content">
                    <ReactMarkdown>{analysis}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            {/* Column Preview */}
            <div className="explorer-columns-section" data-testid="columns-section">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{lang === 'nl' ? 'Kolom Overzicht' : 'Column Overview'}</h3>
              <div className="explorer-columns-grid">
                {activeDataset.columns?.map((col, i) => {
                  const stat = activeDataset.summary?.stats?.[col];
                  const nulls = activeDataset.summary?.null_counts?.[col] || 0;
                  return (
                    <div key={i} className="explorer-col-card" data-testid={`col-${col}`}>
                      <span className="body-small" style={{ fontWeight: 600 }}>{col}</span>
                      {stat ? (
                        <div className="explorer-col-stats">
                          <span className="caption">{lang === 'nl' ? 'gem' : 'avg'}: {stat.mean}</span>
                          <span className="caption">min: {stat.min}</span>
                          <span className="caption">max: {stat.max}</span>
                        </div>
                      ) : (
                        <span className="caption">{lang === 'nl' ? 'Tekst kolom' : 'Text column'}</span>
                      )}
                      {nulls > 0 && <span className="caption" style={{ color: '#8b3a3a' }}>{nulls} {lang === 'nl' ? 'ontbrekend' : 'missing'}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {view === 'query' && activeDataset && (
          <div className="compass-chat-container">
            <div className="compass-chat-messages" data-testid="query-messages">
              {queryMessages.length === 0 && (
                <div className="compass-chat-welcome">
                  <Search size={40} />
                  <h2 className="heading-2">{lang === 'nl' ? 'Vraag alles over je data' : 'Ask anything about your data'}</h2>
                  <p className="body-medium text-secondary">
                    {lang === 'nl' ? `Ik kan vragen beantwoorden over "${activeDataset.filename}" in gewone taal.` : `I can answer questions about "${activeDataset.filename}" in plain language.`}
                  </p>
                  <div className="compass-suggestions" data-testid="query-suggestions">
                    {(lang === 'nl' ? [
                      "Wat zijn de belangrijkste trends in deze data?",
                      "Zijn er afwijkingen of uitschieters?",
                      "Wat zijn de best presterende categorieën?",
                      "Vat de belangrijkste cijfers samen",
                    ] : [
                      "What are the main trends in this data?",
                      "Are there any anomalies or outliers?",
                      "What are the top performing categories?",
                      "Summarize the key metrics",
                    ]).map((s, i) => (
                      <button key={i} className="compass-suggestion-btn" onClick={() => sendQuery(s)} data-testid={`qsug-${i}`}>
                        <Lightbulb size={14} /> {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(Array.isArray(queryMessages) ? queryMessages : []).map((msg, i) => (
                <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                  ) : (
                    <div className="compass-msg-content">{msg.content}</div>
                  )}
                </div>
              ))}
              {querying && (
                <div className="compass-msg compass-msg-assistant">
                  <div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {t.common.analyzing}</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendQuery(queryInput); }} data-testid="query-form">
              <input type="text" value={queryInput} onChange={(e) => setQueryInput(e.target.value)} placeholder={tp.askPlaceholder}
                disabled={querying} className="form-input" data-testid="query-input" />
              <button type="submit" disabled={querying || !((queryInput) == null ? '' : String(queryInput)).trim()} className="btn-primary compass-send-btn" data-testid="query-send">
                <Send size={16} />
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default InsightExplorer;
